import AppIconButton from "../../../components/form/AppIconButton";
import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useTranslation } from "react-i18next";
import { PageEmptyState, PageHeader, PageListContainer } from "../../../components/Page";
import { PageSectionCard } from "../../../components/layout";
import { AppButton } from "../../../components/form";
import { formRowButtonSx } from "../../../lib/ui/formLayout";
import {
  chatQueryExampleService,
  type ChatQueryExample,
} from "../../../services/chatQueryExampleService";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";

function statusColor(status?: string): "default" | "success" | "warning" | "error" | "info" {
  switch (status) {
    case "READY":
      return "success";
    case "FAILED":
      return "error";
    default:
      return "default";
  }
}

export default function ChatQueryExamplesPage() {
  const { t } = useTranslation();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const [items, setItems] = useState<ChatQueryExample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ChatQueryExample | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await chatQueryExampleService.list(0, 100);
      setItems(Array.isArray(page?.content) ? page.content : []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh().catch(console.error);
  }, [refresh]);

  const removeItem = async (item: ChatQueryExample) => {
    if (!item.id) return;
    try {
      await confirmDelete({ itemName: item.title || item.userQuestion || item.id });
      await chatQueryExampleService.remove(item.id);
      await refresh();
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("chatQueryExamples.title"),
            content: t("chatQueryExamples.deleteSuccess"),
          },
        })
      );
    } catch (err) {
      if (isCancelled(err)) return;
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    }
  };

  return (
    <PageListContainer>
      <PageHeader
        title={t("chatQueryExamples.title")}
        subtitle={t("chatQueryExamples.subtitle")}
        actions={
          <AppButton
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => void refresh()}
            disabled={loading}
          >
            {t("common.refresh")}
          </AppButton>
        }
      />

      {error ? (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <PageSectionCard title={t("chatQueryExamples.listTitle")}>
        {loading ? (
          <Stack alignItems="center" py={5}>
            <CircularProgress size={32} />
          </Stack>
        ) : items.length === 0 ? (
          <PageEmptyState message={t("chatQueryExamples.empty")} />
        ) : (
          <TableContainer sx={{ maxWidth: "100%" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("chatQueryExamples.colTitle")}</TableCell>
                  <TableCell>{t("chatQueryExamples.colCreatedBy")}</TableCell>
                  <TableCell>{t("chatQueryExamples.colCreatedAt")}</TableCell>
                  <TableCell>{t("chatQueryExamples.colStatus")}</TableCell>
                  <TableCell align="right">{t("common.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.title || "—"}</TableCell>
                    <TableCell>{item.createdByUsername || "—"}</TableCell>
                    <TableCell>
                      {item.createdDate ? new Date(item.createdDate).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={statusColor(item.status)}
                        label={item.status || "—"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                        <AppIconButton
                          size="small"
                          aria-label={t("chatQueryExamples.viewDetail")}
                          onClick={() => setDetail(item)}
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </AppIconButton>
                        <AppIconButton
                          size="small"
                          aria-label={t("common.delete")}
                          sx={formRowButtonSx}
                          onClick={() => void removeItem(item)}
                        >
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </AppIconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PageSectionCard>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>{detail?.title || t("chatQueryExamples.viewDetail")}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            {t("chat.rag.question")}
          </Typography>
          <Typography variant="body2" paragraph>
            {detail?.userQuestion}
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            {t("chat.rag.summary")}
          </Typography>
          <Typography variant="body2" paragraph>
            {detail?.answerSummary || "—"}
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            {t("chat.rag.queries")}
          </Typography>
          <Stack spacing={1.5}>
            {(detail?.queries || []).map((q, i) => (
              <Box key={i}>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 0.5 }}>
                  <Chip size="small" color="primary" variant="outlined" label={t(`chat.rag.kind.${q.kind.toLowerCase()}`)} />
                  {q.tool ? <Chip size="small" variant="outlined" label={q.tool} /> : null}
                  <Chip size="small" label={q.kind === "OPENSEARCH" ? q.indexName : q.adaptorName || q.adaptorId} />
                </Stack>
                <Box
                  component="pre"
                  sx={{ m: 0, p: 1.5, bgcolor: "action.hover", borderRadius: 1, overflow: "auto", maxHeight: 320, fontSize: 12 }}
                >
                  {typeof q.query === "string" ? q.query : JSON.stringify(q.query, null, 2)}
                </Box>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <AppButton variant="outlined" onClick={() => setDetail(null)}>
            {t("common.close")}
          </AppButton>
        </DialogActions>
      </Dialog>
    </PageListContainer>
  );
}
